import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Divider,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Avatar,
  IconButton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  ShoppingCart as OrderIcon,
  LocationOn as LocationIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import api from '../../utils/api';
import { cacheEndpoints } from '../../utils/apiCache';
import { getCategoryChip } from '../../utils/customerCategory';
import {
  getOrderPayableTotal,
  getOrderSubtotal,
  getOrderShippingCost,
  getOrderDiscount,
  getOrderAppliedCoupon,
} from '../../utils/orderAmount';

const ViewOrder = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { actions } = useApp();
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [storeName, setStoreName] = useState(null);

  useEffect(() => {
    if (orderId) {
      fetchOrder();
    }
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/orders/${orderId}`);
      if (response.data.success) {
        const orderData = response.data.order;
        setOrder(orderData);
        
        // Fetch branch/store name from cache using branch ID
        // Note: In this system, branches and stores are the same entities
        const branchId = orderData.branch_id || orderData.branchId;
        if (branchId) {
          try {
            const branchNameFromCache = await cacheEndpoints.branches.getBranchName(branchId);
            setStoreName(branchNameFromCache);
          } catch (branchError) {
            console.error('Error fetching branch name:', branchError);
            // Don't show error to user, just use branch ID
            setStoreName(null);
          }
        }
      } else {
        setError('Order not found');
      }
    } catch (error) {
      console.error('Error fetching order:', error);
      setError(error.response?.data?.message || 'Error fetching order details');
      actions.showSnackbar('Error fetching order details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else if (timestamp?.toDate) {
      date = timestamp.toDate();
    } else {
      return 'N/A';
    }
    
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  const getOrderStatus = (order) => {
    return order.order_status || order.status || order.orderStatus || 'pending';
  };

  const getOrderAmount = (order) => getOrderPayableTotal(order);

  const getOrderDate = (order) => {
    return order.timeStamp || order.createdAt || order.orderDate || order.timestamp || order.created_at;
  };

  const getOrderId = (order) => {
    return order.orderId || order.id || 'N/A';
  };

  const getDeliveryDate = (order) => {
    return order.delivery_date || 'N/A';
  };

  const getPaymentMethod = (order) => {
    const method = order.payment_method || 'N/A';
    
    // Debug: Log the actual payment method value
    console.log('Payment method value:', method);
    
    if (method === 'N/A') return 'N/A';
    
    const methodLower = method.toLowerCase();
    
    // Handle specific payment method transformations
    if (methodLower.includes('cash') || methodLower.includes('cod') || methodLower === 'cash_on_delivery') {
      return 'COD';
    }
    if (methodLower.includes('wallet') || methodLower.includes('online') || methodLower === 'pay_by_wallet' || methodLower.includes('digital')) {
      return 'Online';
    }
    
    // Default formatting for other methods
    return method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getOrderStatusColor = (status) => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'delivered':
      case 'completed':
        return 'success';
      case 'out_for_delivery':
      case 'processing':
      case 'confirmed':
        return 'warning';
      case 'returned':
        return 'warning'; // Orange color for returned orders
      case 'cancelled':
      case 'failed':
        return 'error';
      case 'pending':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleBack = () => {
    navigate('/super-admin/orders/list');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back to Orders
        </Button>
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Order not found
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back to Orders
        </Button>
      </Box>
    );
  }

  const deliveryTypeRawValue = order.deliveryType ?? order.delivery_type;
  const deliveryTypeRaw =
    deliveryTypeRawValue === null || deliveryTypeRawValue === undefined
      ? ''
      : String(deliveryTypeRawValue);
  const deliveryTypeNormalized = deliveryTypeRaw.trim().toLowerCase();
  const isSelfPickup = deliveryTypeNormalized === 'self_pickup';
  const deliveryTypeLabel =
    deliveryTypeNormalized === 'delivery'
      ? 'Home Delivery'
      : isSelfPickup
        ? 'Self Pickup'
        : (deliveryTypeRaw || 'N/A');

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={handleBack} color="primary">
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Order Details - #{getOrderId(order)}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View complete order information and details
          </Typography>
        </Box>
      </Box>

      {/* Order Info Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Order Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Order Date
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {formatDate(getOrderDate(order))}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Delivery Date
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {getDeliveryDate(order)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box
                sx={{
                  textAlign: 'center',
                  p: 2,
                  backgroundColor: 'grey.50',
                  borderRadius: 2,
                  border: isSelfPickup ? 2 : 1,
                  borderColor: isSelfPickup ? 'warning.main' : 'divider'
                }}
              >
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Delivery Type
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {deliveryTypeLabel}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Time Slot
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {order.time_slot_id || 'N/A'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Platform
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {order.platform || order.order_generation_on || 'N/A'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Branch
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {storeName || order.branch_id || order.branchId || 'N/A'}
                </Typography>
                {storeName && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {order.branch_id || order.branchId}
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Status
                </Typography>
                <Chip 
                  label={getOrderStatus(order).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} 
                  color={getOrderStatus(order).toLowerCase() === 'assigned' ? undefined : getOrderStatusColor(getOrderStatus(order))}
                  size="medium"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '0.875rem',
                    ...(getOrderStatus(order).toLowerCase() === 'assigned'
                      ? { bgcolor: '#2e7d32', color: '#fff', '& .MuiChip-label': { color: '#fff' } }
                      : {})
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Customer Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
            Customer Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Customer Name
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {order.customer_name || order.customerName || order.user?.name || 'N/A'}
                  </Typography>
                  {(() => {
                    const chip = getCategoryChip(order.category);
                    return chip ? (
                      <Chip
                        label={chip.label}
                        color={chip.color}
                        size="small"
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                      />
                    ) : null;
                  })()}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Phone Number
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {order.customer_phone || order.customerPhone || order.user?.mobile || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Products Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <OrderIcon sx={{ mr: 1, color: 'primary.main' }} />
            Products ({order.products?.length || 0})
          </Typography>
          
          {order.products?.map((product, index) => (
            <Box key={index} sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              p: 2, 
              mb: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              backgroundColor: 'background.paper'
            }}>
              <Avatar 
                src={product.imgUrls?.[0]} 
                alt={product.name}
                sx={{ width: 60, height: 60, mr: 2 }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {product.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <Typography component="span" variant="body2" fontWeight="bold" color="primary.main">
                    Qty: {product.count}
                  </Typography>
                  {(product.qty || product.quantity) && (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({product.qty || product.quantity})
                    </Typography>
                  )}
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    × {formatCurrency(product.price)}
                  </Typography>
                </Typography>
                {product.extras && (
                  <Box sx={{ mt: 1 }}>
                    {Object.entries(product.extras).map(([key, value], index) => {
                      const formatValue = (val) => {
                        if (typeof val === 'string') {
                          return val
                            .replace(/([A-Z])/g, ' $1')
                            .replace(/^./, str => str.toUpperCase())
                            .trim();
                        }
                        return val;
                      };
                      
                      return (
                        <Box key={key} sx={{ display: 'inline-block' }}>
                          <Chip 
                            label={formatValue(value)}
                            size="small"
                            variant="filled"
                            sx={{ 
                              mr: 0.5, 
                              mb: 0.5,
                              backgroundColor: 'grey.50',
                              color: 'text.primary',
                              '&:hover': {
                                backgroundColor: 'grey.100'
                              }
                            }}
                          />
                          {(index + 1) % 2 === 0 && <br />}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                {formatCurrency(product.price * product.count)}
              </Typography>
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* Order Total */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <ReceiptIcon sx={{ mr: 1 }} />
            Order Total
          </Typography>
          <Box sx={{ 
            p: 3, 
            backgroundColor: 'grey.50', 
            borderRadius: 2,
            border: 1,
            borderColor: 'divider'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">Subtotal:</Typography>
              <Typography variant="body1" fontWeight="bold">
                {formatCurrency(getOrderSubtotal(order))}
              </Typography>
            </Box>
            {getOrderShippingCost(order) > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Shipping:</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {formatCurrency(getOrderShippingCost(order))}
                </Typography>
              </Box>
            )}
            {getOrderDiscount(order) > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  Coupon Applied: {getOrderAppliedCoupon(order)}
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="success.main">
                  -{formatCurrency(getOrderDiscount(order))}
                </Typography>
              </Box>
            )}
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h5" fontWeight="bold">Total:</Typography>
              <Typography variant="h5" fontWeight="bold">
                {formatCurrency(getOrderPayableTotal(order))}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Instructions Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <OrderIcon sx={{ mr: 1, color: 'primary.main' }} />
            Instructions
          </Typography>
          <Box sx={{ 
            p: 2, 
            backgroundColor: 'grey.50', 
            borderRadius: 2,
            border: 1,
            borderColor: 'divider'
          }}>
            <Typography variant="body1">
              {order.add_instructions || 'No special instructions'}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Delivery Address */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <LocationIcon sx={{ mr: 1, color: 'primary.main' }} />
            Delivery Address {order.orderDistance && `(${(order.orderDistance / 1000).toFixed(1)} km)`}
          </Typography>
          {order.shippingAddressDataa && order.shippingAddressDataa[0] && (
            <Box sx={{ 
              p: 3, 
              backgroundColor: 'grey.50', 
              borderRadius: 2,
              border: 1,
              borderColor: 'divider'
            }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                {order.shippingAddressDataa[0].contact_person_name}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
                {order.shippingAddressDataa[0].address}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
                {order.shippingAddressDataa[0].city}, {order.shippingAddressDataa[0].state} - {order.shippingAddressDataa[0].zip}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {order.shippingAddressDataa[0].country}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Payment Details */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <PaymentIcon sx={{ mr: 1, color: 'text.secondary' }} />
            Payment Details
          </Typography>
          <Box sx={{ 
            p: 2, 
            backgroundColor: 'grey.100', 
            borderRadius: 2,
            border: 1,
            borderColor: 'divider'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Payment Method:</Typography>
              <Typography variant="body2" fontWeight="bold">
                {getPaymentMethod(order)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Payment Status:</Typography>
              <Chip 
                label={order.payment_status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'} 
                color={order.payment_status === 'paid' ? 'success' : 'warning'}
                size="small"
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Delivery Type:</Typography>
              <Typography variant="body2" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                {order.delivery_type || 'N/A'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Back Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back to Orders
        </Button>
      </Box>
    </Box>
  );
};

export default ViewOrder; 